import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '../../../shared/components/SEOHead';
import { apiClient } from '../../../shared/services/apiClient';
import '../styles/seller-register.css';
import artecraftLogo from '../../../assets/img/logo/artecraftindia.jpg';

const sellerSchema = z.object({
    sellerFirstName: z.string().min(2, 'Min 2 characters'),
    sellerLastName:  z.string().min(2, 'Min 2 characters'),
    sellerStoreName: z.string().min(2, 'Min 2 characters'),
    email:           z.string().email('Enter a valid email'),
    mobile:      z.string().min(10, 'Min 10 digits').max(15, 'Max 15 digits').regex(/^\d+$/, 'Digits only'),
    gstNumber:   z.string().length(15, 'Must be 15 characters').regex(/^[A-Z0-9]{15}$/, 'Invalid GST format'),
    categoryId:  z.string().min(1, 'Please select a category'),
    address:     z.string().min(5, 'Enter full address'),
    city:        z.string().min(2, 'Enter city'),
    pincode:     z.string().length(6, 'Must be 6 digits').regex(/^\d{6}$/, 'Digits only'),
    state:       z.string().min(2, 'Enter state'),
    country:     z.string().min(1, 'Required'),
    password:    z.string()
        .min(8, 'Minimum 8 characters')
        .regex(/[A-Z]/, 'Must contain uppercase')
        .regex(/[0-9]/, 'Must contain a number')
        .regex(/[@#$%^&*]/, 'Must contain special character (@#$%^&*)'),
});

type SellerForm = z.infer<typeof sellerSchema>;

interface Category { _id: string; name: string; }

export const SellerRegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState<Category[]>([]);
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        apiClient.get('/seller-categories')
            .then(res => setCategories(res.data as Category[]))
            .catch(() => {});
    }, []);

    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<SellerForm>({
        resolver: zodResolver(sellerSchema),
        defaultValues: { country: 'India' },
    });

    const onSubmit = async (data: SellerForm) => {
        setServerError('');
        try {
            await apiClient.post('/auth/seller-register', data);
            setSuccess(true);
        } catch (err: any) {
            setServerError(err.response?.data?.message ?? 'Registration failed. Please try again.');
        }
    };

    if (success) {
        return (
            <div className="sreg-success-wrap">
                <div className="sreg-success-card">
                    <div className="sreg-success-icon">✓</div>
                    <h2>Application Submitted!</h2>
                    <p>
                        Your seller application is under review. We've sent a verification
                        link to your email — please confirm it to proceed.
                    </p>
                    <ul className="sreg-success-steps">
                        <li className="sreg-success-step">
                            <span>📧</span>
                            <span>Check your inbox and click the verification link</span>
                        </li>
                        <li className="sreg-success-step">
                            <span>⏳</span>
                            <span>Our team will review and approve your application</span>
                        </li>
                        <li className="sreg-success-step">
                            <span>🚀</span>
                            <span>Once approved, log in and start listing products</span>
                        </li>
                    </ul>
                    <button className="sreg-btn" onClick={() => navigate('/login')}>
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <SEOHead seo={{
                title: 'Seller Registration | Arte Craft India',
                description: 'Register as a seller on Arte Craft India marketplace.',
                canonical: `${window.location.origin}/seller/register`,
                noIndex: true,
            }} />

            <div className="sreg-page">

                {/* ── Brand panel ── */}
                <aside className="sreg-brand">
                    <div className="sreg-brand-logo">
                        <img src={artecraftLogo} alt="Arte Craft India" className="sreg-brand-img" />
                    </div>

                    <h2>Grow your craft <em>business</em> online</h2>
                    <p className="sreg-brand-sub">
                        Join thousands of artisans selling handcrafted products to
                        customers across India.
                    </p>

                    <ul className="sreg-perks">
                        <li className="sreg-perk">
                            <div className="sreg-perk-icon">🏪</div>
                            <div className="sreg-perk-text">
                                <strong>Your own seller storefront</strong>
                                <span>Showcase your products with a dedicated profile page</span>
                            </div>
                        </li>
                        <li className="sreg-perk">
                            <div className="sreg-perk-icon">📦</div>
                            <div className="sreg-perk-text">
                                <strong>Easy inventory management</strong>
                                <span>List, update, and track products from one dashboard</span>
                            </div>
                        </li>
                        <li className="sreg-perk">
                            <div className="sreg-perk-icon">💰</div>
                            <div className="sreg-perk-text">
                                <strong>Fast, reliable payouts</strong>
                                <span>Earnings settled directly to your bank account</span>
                            </div>
                        </li>
                        <li className="sreg-perk">
                            <div className="sreg-perk-icon">📊</div>
                            <div className="sreg-perk-text">
                                <strong>Sales analytics & insights</strong>
                                <span>Understand your buyers and grow with data</span>
                            </div>
                        </li>
                    </ul>

                    <div className="sreg-brand-footer">
                        © 2026 Arte Craft India. All rights reserved.
                    </div>
                </aside>

                {/* ── Form panel ── */}
                <div className="sreg-form-panel">
                    <div className="sreg-card">

                        <div className="sreg-header">
                            <h1>Create Seller Account</h1>
                            <p>Fill in your details — approval takes 1–2 business days</p>
                        </div>

                        {/* Step indicators */}
                        <div className="sreg-steps">
                            <div className="sreg-step active">
                                <div className="sreg-step-num">1</div>
                                <span className="sreg-step-label">Store Info</span>
                            </div>
                            <div className="sreg-step active">
                                <div className="sreg-step-num">2</div>
                                <span className="sreg-step-label">Address</span>
                            </div>
                            <div className="sreg-step active">
                                <div className="sreg-step-num">3</div>
                                <span className="sreg-step-label">Security</span>
                            </div>
                        </div>

                        {serverError && (
                            <div className="sreg-error">{serverError}</div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>

                            {/* ── Section 1: Store Information ── */}
                            <div className="sreg-section-header">
                                <div className="sreg-section-num">1</div>
                                <span className="sreg-section-title">Store Information</span>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.sellerFirstName ? ' has-error' : ''}`}>
                                        <span className="input-icon">👤</span>
                                        <input {...register('sellerFirstName')} placeholder="First name" autoComplete="given-name" />
                                    </div>
                                    {errors.sellerFirstName && <span className="field-error">{errors.sellerFirstName.message}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Last Name <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.sellerLastName ? ' has-error' : ''}`}>
                                        <span className="input-icon">👤</span>
                                        <input {...register('sellerLastName')} placeholder="Last name" autoComplete="family-name" />
                                    </div>
                                    {errors.sellerLastName && <span className="field-error">{errors.sellerLastName.message}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Store Name <span className="req">*</span></label>
                                <div className={`input-wrap${errors.sellerStoreName ? ' has-error' : ''}`}>
                                    <span className="input-icon">🏪</span>
                                    <input {...register('sellerStoreName')} placeholder="Your store or brand name" autoComplete="organization" />
                                </div>
                                {errors.sellerStoreName && <span className="field-error">{errors.sellerStoreName.message}</span>}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email Address <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.email ? ' has-error' : ''}`}>
                                        <span className="input-icon">✉</span>
                                        <input type="email" {...register('email')} placeholder="business@email.com" autoComplete="email" />
                                    </div>
                                    {errors.email && <span className="field-error">{errors.email.message}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Mobile Number <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.mobile ? ' has-error' : ''}`}>
                                        <span className="input-icon">📱</span>
                                        <input type="tel" {...register('mobile')} placeholder="10–15 digit number" />
                                    </div>
                                    {errors.mobile && <span className="field-error">{errors.mobile.message}</span>}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>GST Number <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.gstNumber ? ' has-error' : ''}`}>
                                        <span className="input-icon">🪪</span>
                                        <input
                                            {...register('gstNumber')}
                                            placeholder="15-char GST number"
                                            onInput={e => {
                                                (e.target as HTMLInputElement).value =
                                                    (e.target as HTMLInputElement).value.toUpperCase();
                                            }}
                                        />
                                    </div>
                                    {errors.gstNumber
                                        ? <span className="field-error">{errors.gstNumber.message}</span>
                                        : <span className="input-hint">e.g. 22AAAAA0000A1Z5</span>
                                    }
                                </div>
                                <div className="form-group">
                                    <label>Product Category <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.categoryId ? ' has-error' : ''}`}>
                                        <span className="input-icon">🗂</span>
                                        <select {...register('categoryId')}>
                                            <option value="">Select category</option>
                                            {categories.map(c => (
                                                <option key={c._id} value={c._id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.categoryId && <span className="field-error">{errors.categoryId.message}</span>}
                                </div>
                            </div>

                            {/* ── Section 2: Business Address ── */}
                            <div className="sreg-section-header">
                                <div className="sreg-section-num">2</div>
                                <span className="sreg-section-title">Business Address</span>
                            </div>

                            <div className="form-group">
                                <label>Street Address <span className="req">*</span></label>
                                <div className={`input-wrap${errors.address ? ' has-error' : ''}`}>
                                    <span className="input-icon">🏠</span>
                                    <input {...register('address')} placeholder="Building, street, locality" autoComplete="street-address" />
                                </div>
                                {errors.address && <span className="field-error">{errors.address.message}</span>}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>City <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.city ? ' has-error' : ''}`}>
                                        <span className="input-icon">🏙</span>
                                        <input {...register('city')} placeholder="City" autoComplete="address-level2" />
                                    </div>
                                    {errors.city && <span className="field-error">{errors.city.message}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Pincode <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.pincode ? ' has-error' : ''}`}>
                                        <span className="input-icon">📍</span>
                                        <input {...register('pincode')} placeholder="6-digit pincode" inputMode="numeric" autoComplete="postal-code" />
                                    </div>
                                    {errors.pincode && <span className="field-error">{errors.pincode.message}</span>}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>State <span className="req">*</span></label>
                                    <div className={`input-wrap${errors.state ? ' has-error' : ''}`}>
                                        <span className="input-icon">🗺</span>
                                        <input {...register('state')} placeholder="State" autoComplete="address-level1" />
                                    </div>
                                    {errors.state && <span className="field-error">{errors.state.message}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Country</label>
                                    <div className="input-wrap">
                                        <span className="input-icon">🌐</span>
                                        <input {...register('country')} autoComplete="country-name" />
                                    </div>
                                </div>
                            </div>

                            {/* ── Section 3: Account Security ── */}
                            <div className="sreg-section-header">
                                <div className="sreg-section-num">3</div>
                                <span className="sreg-section-title">Account Security</span>
                            </div>

                            <div className="form-group">
                                <label>Password <span className="req">*</span></label>
                                <div className={`input-wrap has-toggle${errors.password ? ' has-error' : ''}`}>
                                    <span className="input-icon">🔒</span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        {...register('password')}
                                        placeholder="Min 8 chars, uppercase, number, symbol"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="pw-toggle"
                                        onClick={() => setShowPassword(p => !p)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? '🙈' : '👁'}
                                    </button>
                                </div>
                                {errors.password && <span className="field-error">{errors.password.message}</span>}
                            </div>

                            <button type="submit" className="sreg-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting application…' : 'Submit Application →'}
                            </button>
                        </form>

                        <div className="sreg-footer">
                            Already have an account? <Link to="/login">Sign in</Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
